import React, { useEffect, useState } from 'react';
import { FaPhoneSlash, FaTimes, FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import { MdPhoneLocked } from 'react-icons/md';
import { BsFillTelephoneMinusFill } from 'react-icons/bs';

const TERMINAL_STATUSES = new Set(['ended', 'disconnected', 'failed']);

const formatDuration = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return `${mm} min, ${ss} sec`;
};

const STATUS_LABEL = {
  connecting: 'Connecting…',
  ringing: 'Ringing…',
  connected: 'Connected',
  disconnecting: 'Ending…',
  ended: 'Call ended',
  disconnected: 'Disconnected',
  failed: 'Call failed'
};

const STATUS_COLOR = {
  connecting: '#6b7280',
  ringing: '#f59e0b',
  connected: '#16a34a',
  disconnecting: '#6b7280',
  ended: '#6b7280',
  disconnected: '#6b7280',
  failed: '#dc2626'
};

function OutboundCallPill({
  studentName,
  toNumber,
  status,
  muted,
  // Multi-agent / conference state. All optional — pre-feature pills work as before.
  unlocked = false,
  isOriginator = true,
  participantCount = 1,
  onToggleMute,
  onEndCall,
  onUnlock,
  onLeave,
  onEndForEveryone,
  onClose
}) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (status !== 'connected') return undefined;
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  const statusLabel = STATUS_LABEL[status] || status;
  const statusColor = STATUS_COLOR[status] || '#6b7280';
  const primaryLabel = studentName || toNumber || 'Outgoing call';

  return (
    <div
      style={{
        position: 'fixed',
        top: '90px',
        right: '20px',
        // Stay below the global ReactTooltip's zIndex (100, set in App.jsx)
        // so tooltips on Mute / End call buttons render in front of the pill.
        zIndex: 90,
        background: '#ffffff',
        borderRadius: '9999px',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
        padding: '8px 8px 8px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        border: '1px solid #e5e7eb',
        minWidth: '340px'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, gap: '6px' }}>
        <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }} title={primaryLabel}>
          {primaryLabel}
        </span>
        {studentName && toNumber && (
          <span style={{ fontSize: '11px', color: '#6b7280' }}>{toNumber}</span>
        )}
        <span
          style={{
            fontSize: '12px',
            color: statusColor,
            display: 'flex',
            gap: '6px',
            alignItems: 'center'
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: '6px',
              height: '6px',
              borderRadius: '9999px',
              background: statusColor
            }}
          />
          {statusLabel}
          {status === 'connected' && (
            <span style={{ color: '#6b7280', marginLeft: '4px' }}>· {formatDuration(elapsed)}</span>
          )}
        </span>
      </div>
      {status === 'connected' && (
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={onToggleMute}
            aria-label={muted ? 'Unmute' : 'Mute'}
            data-tooltip-id="my-tooltip"
            data-tooltip-content={muted ? 'Unmute' : 'Mute'}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '9999px',
              background: muted ? '#fee2e2' : '#f3f4f6',
              color: muted ? '#b91c1c' : '#374151',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
              flexShrink: 0
            }}
          >
            {muted ? <FaMicrophoneSlash size={16} /> : <FaMicrophone size={16} />}
          </button>
          {isOriginator && !unlocked && onUnlock && (
            <button
              type="button"
              onClick={onUnlock}
              aria-label="Move call to conference"
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Move this call to a conference so other ops users can join"
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '9999px',
                background: '#fef3c7',
                color: '#92400e',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
                flexShrink: 0
              }}
            >
              <MdPhoneLocked size={18} />
            </button>
          )}
          {unlocked && participantCount > 1 && onLeave && (
            <button
              type="button"
              onClick={onLeave}
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Leave the call (continues for everyone else)"
              style={{
                background: '#f3f4f6',
                color: '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: '9999px',
                padding: '8px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500
              }}
            >
              <BsFillTelephoneMinusFill size={14} />
              Leave
            </button>
          )}
          <button
            type="button"
            onClick={unlocked && onEndForEveryone ? onEndForEveryone : onEndCall}
            data-tooltip-id="my-tooltip"
            data-tooltip-content={unlocked ? 'End call for everyone' : 'Hang up'}
            style={{
              background: '#ef4444',
              color: '#ffffff',
              border: 'none',
              borderRadius: '9999px',
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500
            }}
          >
            <FaPhoneSlash size={14} style={{ transform: 'rotate(90deg)' }} />
            End call
          </button>
        </div>
      )}
      {TERMINAL_STATUSES.has(status) && (
        <button
          type="button"
          onClick={onClose || onEndCall}
          aria-label="Close"
          style={{
            marginLeft: 'auto',
            background: '#111827',
            color: '#ffffff',
            border: 'none',
            borderRadius: '9999px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
            flexShrink: 0
          }}
        >
          <FaTimes size={16} color="#ffffff" />
        </button>
      )}
    </div>
  );
}

export default OutboundCallPill;
